import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
    Droplets, 
    ExternalLink, 
    TrendingUp, 
    Shield, 
    ArrowRight,
    Coins,
    Zap,
    BarChart3,
    Info,
    Copy,
    CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const B4P_TOKEN = {
    address: "0x92DE221A94C12920C896D08ebEfA6Bb98c8815aE",
    symbol: "B4P",
    name: "Bet4PeerToken",
    decimals: 18,
    chainId: 8453,
    totalSupply: "100,000,000"
};

const AERODROME_URL = `https://aerodrome.finance/swap?from=eth&to=${B4P_TOKEN.address}`;
const AERODROME_LP_URL = `https://aerodrome.finance/deposit?token0=eth&token1=${B4P_TOKEN.address}`;
const BASESCAN_URL = `https://basescan.org/token/${B4P_TOKEN.address}`;

function CopyAddress({ address }) {
    const [copied, setCopied] = useState(false);
    
    const handleCopy = () => {
        navigator.clipboard.writeText(address);
        setCopied(true);
        toast.success('Address copied!');
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <button 
            onClick={handleCopy}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 hover:border-[#00FF66]/30 transition-all font-mono text-sm text-white/70 hover:text-white"
            data-testid="copy-token-address"
        >
            {address.slice(0, 10)}...{address.slice(-8)}
            {copied ? <CheckCircle className="w-4 h-4 text-[#00FF66]" /> : <Copy className="w-4 h-4" />}
        </button>
    );
}

function LiquidityStep({ number, title, description, action, actionUrl, icon: Icon }) {
    return (
        <div className={`flex gap-4 p-5 rounded-xl bg-white/[0.02] border border-white/5 card-hover animate-fade-in-up delay-${number}`}>
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#00FF66]/10 flex items-center justify-center">
                <span className="font-mono font-bold text-[#00FF66]">{number}</span>
            </div>
            <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-4 h-4 text-[#00FF66]" />
                    <h3 className="font-semibold text-white">{title}</h3>
                </div>
                <p className="text-sm text-white/50 mb-3">{description}</p>
                {actionUrl && (
                    <a href={actionUrl} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" className="bg-[#00FF66] text-[#0A0A0C] hover:bg-[#00CC52] btn-press">
                            {action}
                            <ExternalLink className="w-3 h-3 ml-2" />
                        </Button>
                    </a>
                )}
            </div>
        </div>
    );
}

export default function LiquidityPage() {
    const { isConnected, chainId } = useWeb3();
    const [b4pStats, setB4pStats] = useState(null);

    useEffect(() => {
        async function fetchStats() {
            try {
                const res = await fetch(`${API_URL}/api/b4p/stats`);
                if (res.ok) {
                    const data = await res.json();
                    setB4pStats(data);
                }
            } catch (statsErr) {
                console.warn('B4P stats unavailable:', statsErr?.message);
            }
        }
        fetchStats();
    }, []);

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 page-enter" data-testid="liquidity-page">
            {/* Header */}
            <div className="text-center mb-10 animate-fade-in-down">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00FF66]/10 border border-[#00FF66]/20 mb-4">
                    <Droplets className="w-4 h-4 text-[#00FF66]" />
                    <span className="text-[#00FF66] text-sm font-medium">Aerodrome Finance on Base</span>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
                    <span className="text-[#00FF66]">B4P</span> Liquidity
                </h1>
                <p className="text-white/50 max-w-lg mx-auto">
                    Provide liquidity for the B4P token on Aerodrome (Base DEX) and earn trading fees
                </p>
            </div>

            {/* Token Info Card */}
            <Card className="bg-gradient-to-r from-[#00FF66]/5 to-[#9B6FE8]/5 border-[#00FF66]/20 mb-8 animate-fade-in-up delay-1">
                <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-[#FFD700]/20 flex items-center justify-center">
                                <Coins className="w-7 h-7 text-[#FFD700]" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-white">{B4P_TOKEN.name}</h2>
                                <div className="flex items-center gap-3 text-sm text-white/50">
                                    <span>Symbol: <span className="text-[#FFD700] font-semibold">${B4P_TOKEN.symbol}</span></span>
                                    <span>Chain: <span className="text-[#0052FF] font-semibold">Base</span></span>
                                    <span>Supply: <span className="text-white/70">{B4P_TOKEN.totalSupply}</span></span>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <CopyAddress address={B4P_TOKEN.address} />
                            <a href={BASESCAN_URL} target="_blank" rel="noopener noreferrer">
                                <Button variant="outline" size="sm" className="border-white/10 text-white/60 hover:text-white btn-press">
                                    <ExternalLink className="w-4 h-4 mr-1" />
                                    BaseScan
                                </Button>
                            </a>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <Card className="bg-white/[0.02] border-white/5 card-hover animate-fade-in-up delay-1">
                    <CardContent className="p-4 text-center">
                        <Coins className="w-5 h-5 text-[#FFD700] mx-auto mb-2" />
                        <div className="text-2xl font-bold text-white stat-number">{B4P_TOKEN.totalSupply.split(',')[0]}M</div>
                        <div className="text-xs text-white/40">Total Supply</div>
                    </CardContent>
                </Card>
                <Card className="bg-white/[0.02] border-white/5 card-hover animate-fade-in-up delay-2">
                    <CardContent className="p-4 text-center">
                        <BarChart3 className="w-5 h-5 text-[#00FF66] mx-auto mb-2" />
                        <div className="text-2xl font-bold text-[#00FF66] stat-number">
                            {b4pStats?.total_distributed?.toLocaleString() || '0'}
                        </div>
                        <div className="text-xs text-white/40">Distributed</div>
                    </CardContent>
                </Card>
                <Card className="bg-white/[0.02] border-white/5 card-hover animate-fade-in-up delay-3">
                    <CardContent className="p-4 text-center">
                        <TrendingUp className="w-5 h-5 text-[#9B6FE8] mx-auto mb-2" />
                        <div className="text-2xl font-bold text-[#9B6FE8] stat-number">
                            Phase {b4pStats?.current_phase || 1}
                        </div>
                        <div className="text-xs text-white/40">Halving Phase</div>
                    </CardContent>
                </Card>
                <Card className="bg-white/[0.02] border-white/5 card-hover animate-fade-in-up delay-4">
                    <CardContent className="p-4 text-center">
                        <Shield className="w-5 h-5 text-[#0052FF] mx-auto mb-2" />
                        <div className="text-2xl font-bold text-[#0052FF] stat-number">Base</div>
                        <div className="text-xs text-white/40">Network</div>
                    </CardContent>
                </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left: Steps */}
                <div className="lg:col-span-2 space-y-4">
                    <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2 animate-fade-in-up">
                        <Zap className="w-5 h-5 text-[#FFD700]" />
                        How to Add Liquidity
                    </h2>

                    <LiquidityStep
                        number={1}
                        title="Get B4P Tokens"
                        description="Earn B4P through betting, daily bonuses, referrals, or bridge your off-chain balance to on-chain tokens."
                        action="Go to B4P Hub"
                        actionUrl="/b4p"
                        icon={Coins}
                    />
                    <LiquidityStep
                        number={2}
                        title="Connect to Aerodrome"
                        description="Aerodrome is the largest DEX on Base. Connect your wallet and make sure you're on Base network."
                        action="Open Aerodrome"
                        actionUrl={AERODROME_URL}
                        icon={Droplets}
                    />
                    <LiquidityStep
                        number={3}
                        title="Add Liquidity (ETH/B4P)"
                        description="Deposit equal value of ETH and B4P tokens into the liquidity pool to earn trading fees from every swap."
                        action="Add Liquidity"
                        actionUrl={AERODROME_LP_URL}
                        icon={TrendingUp}
                    />
                    <LiquidityStep
                        number={4}
                        title="Earn Rewards"
                        description="As a liquidity provider, you earn a share of all trading fees. The more liquidity you provide, the more you earn."
                        icon={BarChart3}
                    />
                </div>

                {/* Right: Quick Actions */}
                <div className="space-y-4">
                    {/* Swap Card */}
                    <Card className="bg-white/[0.02] border-white/5 animate-fade-in-right delay-1">
                        <CardHeader>
                            <CardTitle className="text-lg text-white flex items-center gap-2">
                                <ArrowRight className="w-5 h-5 text-[#00FF66]" />
                                Quick Swap
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <p className="text-sm text-white/50">Swap ETH for B4P tokens on Aerodrome</p>
                            <a href={AERODROME_URL} target="_blank" rel="noopener noreferrer" className="block">
                                <Button className="w-full bg-[#00FF66] text-[#0A0A0C] hover:bg-[#00CC52] btn-press" data-testid="swap-btn">
                                    Swap on Aerodrome
                                    <ExternalLink className="w-4 h-4 ml-2" />
                                </Button>
                            </a>
                        </CardContent>
                    </Card>

                    {/* LP Card */}
                    <Card className="bg-white/[0.02] border-white/5 animate-fade-in-right delay-2">
                        <CardHeader>
                            <CardTitle className="text-lg text-white flex items-center gap-2">
                                <Droplets className="w-5 h-5 text-[#9B6FE8]" />
                                Provide Liquidity
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <p className="text-sm text-white/50">Add ETH + B4P to the pool and earn fees</p>
                            <a href={AERODROME_LP_URL} target="_blank" rel="noopener noreferrer" className="block">
                                <Button className="w-full bg-[#9B6FE8] text-white hover:bg-[#8A5FD7] btn-press" data-testid="lp-btn">
                                    Add Liquidity
                                    <ExternalLink className="w-4 h-4 ml-2" />
                                </Button>
                            </a>
                        </CardContent>
                    </Card>

                    {/* Info Card */}
                    <Alert className="border-[#FFD700]/20 bg-[#FFD700]/5 animate-fade-in-right delay-3">
                        <Info className="w-4 h-4 text-[#FFD700]" />
                        <AlertDescription className="text-white/60 text-sm">
                            <strong className="text-[#FFD700]">Why Aerodrome?</strong><br />
                            Aerodrome is the #1 DEX on Base with the deepest liquidity. 
                            It uses a ve(3,3) model that rewards long-term liquidity providers.
                        </AlertDescription>
                    </Alert>

                    {!isConnected && (
                        <Alert className="border-white/10 bg-white/5 animate-fade-in-right delay-4">
                            <Shield className="w-4 h-4 text-white/60" />
                            <AlertDescription className="text-white/60 text-sm">
                                Connect your wallet to interact with the DEX
                            </AlertDescription>
                        </Alert>
                    )}
                </div>
            </div>
        </div>
    );
}
